import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import FormHeader from "../common/FormHeader";
import "./formTitle.css";
import "./themePlaceholder.css";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function nowLocalDatetime() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
}

function toHHmm(value) {
  if (!value) return "";
  const match = String(value).match(/T(\d{2}):(\d{2})/);
  if (match) return `${match[1]}:${match[2]}`;
  const parts = String(value).split(":");
  if (parts.length >= 2) {
    return `${String(parts[0]).padStart(2, "0")}:${String(parts[1]).padStart(2, "0")}`;
  }
  return "";
}

export default function OnCallEmergencyForm() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [worksheetRow, setWorksheetRow] = useState(null);
  const [form, setForm] = useState({
    timeOfCallout: nowLocalDatetime(),
    onDuty: nowLocalDatetime(),
    offDuty: nowLocalDatetime(),
    commutingTime: "",
    calloutReason: "",
  });

  const worksheetEntryId =
    searchParams.get("worksheetEntryId") || searchParams.get("id") || "";

  async function loadWorksheetRow() {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login to continue");
      setLoading(false);
      return;
    }
    if (!worksheetEntryId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(getBaseUrl() + "/api/myworksheet", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.message || "Failed to load worksheet");
        return;
      }
      const rows = Array.isArray(data.data) ? data.data : [];
      const row = rows.find((item) => String(item.id) === String(worksheetEntryId)) || null;
      setWorksheetRow(row);
    } catch {
      toast.error("Failed to load worksheet");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadWorksheetRow();
  }, [worksheetEntryId]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!worksheetEntryId || !worksheetRow) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        worksheetEntryId,
        timeOfCallout: toHHmm(form.timeOfCallout),
        onDuty: toHHmm(form.onDuty),
        offDuty: toHHmm(form.offDuty),
        commutingTime: String(form.commutingTime).trim(),
        calloutReason: String(form.calloutReason).trim(),
      };
      const res = await fetch(getBaseUrl() + "/api/oncall-emergency", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.message || "Failed to submit form");
        return;
      }
      toast.success("On-call emergency form submitted");
      setForm({
        timeOfCallout: nowLocalDatetime(),
        onDuty: nowLocalDatetime(),
        offDuty: nowLocalDatetime(),
        commutingTime: "",
        calloutReason: "",
      });
      await loadWorksheetRow();
    } catch {
      toast.error("Failed to submit form");
    } finally {
      setSubmitting(false);
    }
  }

  const onCallCount = worksheetRow?.onCallCount ?? 0;
  const canSubmit =
    worksheetRow?.standbyActive === true && onCallCount < 2 && Boolean(worksheetEntryId);

  return (
    <div style={{ minHeight: "100vh", overflowY: "auto", background: "#f5f7fa" }}>
      <FormHeader />
      <div className="container py-4" style={{ maxWidth: "640px" }}>
        <div className="card shadow-sm rounded-4 p-4">
          <h2
            className="text-center text-white py-2 mb-3 rounded"
            style={{ backgroundColor: "#8b1634", fontSize: "1.5rem" }}
          >
            On-Call Emergency
          </h2>

          {loading ? (
            <p className="text-center text-muted py-4">Loading...</p>
          ) : !worksheetEntryId ? (
            <p className="text-center text-muted py-4">Worksheet entry id is required.</p>
          ) : !worksheetRow ? (
            <p className="text-center text-muted py-4">Worksheet entry not found.</p>
          ) : !canSubmit ? (
            <p className="text-center text-muted py-4">
              {worksheetRow.standbyActive !== true
                ? "You are not on standby for this day."
                : "SLA cap reached: 2 callouts per worksheet entry max."}
            </p>
          ) : (
            <>
              <div
                style={{
                  background: "#fff5f5",
                  border: "1px solid #fecaca",
                  borderRadius: "10px",
                  padding: "14px 16px",
                  marginBottom: "20px",
                }}
              >
                <div style={{ fontWeight: 700, color: "#991b1b", marginBottom: "6px" }}>On-call callout</div>
                <div style={{ fontSize: "14px", color: "#4a5568" }}>Site: {worksheetRow.siteName || "—"}</div>
                <div style={{ fontSize: "13px", color: "#64748b", marginTop: "6px" }}>
                  Callouts logged: {onCallCount} / 2
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Time of callout</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    name="timeOfCallout"
                    value={form.timeOfCallout}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">On duty</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    name="onDuty"
                    value={form.onDuty}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Off duty</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    name="offDuty"
                    value={form.offDuty}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Commuting time (minutes)</label>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    name="commutingTime"
                    value={form.commutingTime}
                    onChange={handleChange}
                    required
                    placeholder="e.g. 30"
                  />
                </div>
                <div className="mb-4">
                  <label className="form-label fw-semibold">Callout reason</label>
                  <textarea
                    className="form-control"
                    name="calloutReason"
                    rows={4}
                    value={form.calloutReason}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    placeholder="Describe the emergency callout and location"
                  />
                </div>
                <button
                  type="submit"
                  className="btn w-100 text-white"
                  style={{ backgroundColor: "#8b1634" }}
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Submit On-Call Form"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
